import { useRef, useState } from 'react';
import { AlertCircle, Clapperboard, Download, Loader2, Sparkles, Video } from 'lucide-react';
import { apiGenerateVideo } from '../api/client';

const ASPECT_RATIOS = ['16:9', '9:16', '1:1'];

function AiVideoPage({ t, onToast }) {
  const [prompt, setPrompt]           = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [loading, setLoading]       = useState(false);
  const [videoUrl, setVideoUrl]     = useState('');
  const [error, setError]           = useState('');
  const [elapsed, setElapsed]       = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const startTimer = () => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
  };
  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;
    setError('');
    setVideoUrl('');
    setLoading(true);
    startTimer();
    try {
      const url = await apiGenerateVideo({ prompt: prompt.trim(), aspectRatio });
      setVideoUrl(url);
      onToast?.('Video generated successfully!', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Video generation failed.';
      setError(msg);
      onToast?.(msg, 'error');
    } finally {
      setLoading(false);
      stopTimer();
    }
  };

  const handleDownload = () => {
    if (!videoUrl) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `ai-video-${Date.now()}.mp4`;
    a.click();
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const aspectClass = aspectRatio === '9:16'
    ? 'aspect-[9/16] max-h-[480px]'
    : aspectRatio === '1:1'
    ? 'aspect-square max-h-[480px]'
    : 'aspect-video';

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">

      {/* Hero banner */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1A1A1A] via-amber-500 to-pink-500 p-6 text-white shadow-xl dark:border-slate-800">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15">
            <Video size={28} />
          </span>
          <div>
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-0.5 text-xs font-semibold">
              <Sparkles size={12} />
              {t('navAIVideo')}
            </div>
            <h1 className="text-2xl font-bold md:text-3xl">{t('aiVideoHeroTitle')}</h1>
            <p className="mt-1 text-sm text-violet-100">{t('aiVideoHeroBody')}</p>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">

        {/* Left — form + result */}
        <div className="flex flex-col gap-5">

          {/* Prompt form */}
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
              <Clapperboard size={20} className="text-brand-500" />
              {t('aiVideoPromptLabel')}
            </h2>
            <form onSubmit={handleGenerate} className="flex flex-col gap-4">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={4}
                placeholder={t('aiVideoPromptPlaceholder')}
                className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-brand-500 dark:focus:ring-brand-900/40"
              />

              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('aiVideoAspectRatio')}</label>
                  <div className="flex gap-2">
                    {ASPECT_RATIOS.map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setAspectRatio(r)}
                        className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
                          aspectRatio === r
                            ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              <button
                type="submit"
                disabled={loading || !prompt.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/20 transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {t('aiVideoGenerating')} {fmtTime(elapsed)}
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    {t('aiVideoGenerate')}
                  </>
                )}
              </button>
            </form>
          </section>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Loading placeholder */}
          {loading && (
            <div className={`w-full ${aspectClass} overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800`}>
              <div className="flex h-full flex-col items-center justify-center gap-4 text-slate-400">
                <div className="relative flex h-20 w-20 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-20" />
                  <Loader2 size={36} className="animate-spin text-brand-500" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('aiVideoGenerating')} {fmtTime(elapsed)}</p>
                  <p className="mt-1 text-xs text-slate-400">{t('aiVideoWaitHint')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Video result */}
          {videoUrl && !loading && (
            <div className="flex flex-col gap-3">
              <div className={`w-full overflow-hidden rounded-3xl border border-slate-200 bg-black shadow-lg dark:border-slate-700 ${aspectClass}`}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-2 self-start rounded-2xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950/30 dark:text-brand-300"
              >
                <Download size={16} />
                {t('aiVideoDownload')}
              </button>
            </div>
          )}
        </div>

        {/* Right — tips */}
        <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-4 text-base font-bold text-slate-900 dark:text-white">{t('aiVideoTipsTitle')}</h2>
          <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            {['aiVideoTip1','aiVideoTip2','aiVideoTip3','aiVideoTip4'].map(key => (
              <li key={key} className="flex gap-2">
                <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-brand-100 text-center text-[10px] font-bold leading-4 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">✦</span>
                {t(key)}
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
            {t('aiVideoDisclaimer')}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default AiVideoPage;
